import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import { useSamples } from "./SamplesContext";

const DWELL = 6000;

export default function SampleCarousel() {
  const { samples, current, select, play, playing } = useSamples();
  const [paused, setPaused] = useState(false);
  const n = samples.length;
  const go = (d) => n && select((current + d + n) % n);

  useEffect(() => {
    if (paused || playing || !n || matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = setTimeout(() => go(1), DWELL);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, paused, playing, n]);

  const s = samples[current];
  return (
    <section className="glass-card w-full rounded-3xl p-4 text-[var(--cream)]" data-testid="sample-carousel" onPointerEnter={() => setPaused(true)} onPointerLeave={() => setPaused(false)}>
      <div key={current} className="rise flex items-center gap-3" style={{ "--d": 0 }}>
        <button onClick={() => play(current)} className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-[var(--cream)] text-[var(--violet)] transition-transform hover:scale-105" data-testid="carousel-play-btn" aria-label="Play this sample">
          <Play size={20} className="ml-0.5" />
        </button>
        <div className="min-w-0">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--orange)]">Sample 0{current + 1}</span>
          <h3 className="truncate text-base font-semibold">{s ? s.label : "Loading…"}</h3>
          <p className="truncate text-xs text-[var(--cream)]/60">{s?.patient} · {s?.caller}</p>
        </div>
      </div>
      <footer className="mt-3 flex items-center gap-2">
        <div className="flex flex-1 gap-1.5">
          {samples.map((x, k) => (
            <button key={x.key} onClick={() => select(k)} aria-label={`Sample ${k + 1}`} data-testid={`carousel-dot-${x.key}`} className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-white/15">
              {k === current && <i key={`${current}-${paused}-${playing}`} className="progress-bar absolute inset-0 bg-[var(--orange)]" style={{ animationDuration: `${DWELL}ms`, animationPlayState: paused || playing ? "paused" : "running" }} />}
            </button>
          ))}
        </div>
        <button onClick={() => go(-1)} className="grid h-8 w-8 place-items-center rounded-lg hover:bg-white/10" aria-label="Previous sample" data-testid="carousel-prev"><ChevronLeft size={16} /></button>
        <button onClick={() => go(1)} className="grid h-8 w-8 place-items-center rounded-lg hover:bg-white/10" aria-label="Next sample" data-testid="carousel-next"><ChevronRight size={16} /></button>
      </footer>
    </section>
  );
}

const WORDS = ["Every dose", "ప్రతి మందు", "On time", "సమయానికి", "Any phone", "Any age", "Any language"];

export function Ticker() {
  const half = [...WORDS, ...WORDS, ...WORDS];
  const row = (key) =>
    half.map((w, i) => (
      <span key={`${key}${i}`} className={`flex items-center gap-6 px-6 text-lg ${/[\u0C00-\u0C7F]/.test(w) ? "font-te" : i % 2 ? "" : "font-serif-i text-2xl"}`}>
        {w}
        <b className="text-[var(--orange)]" aria-hidden="true">✦</b>
      </span>
    ));
  return (
    <div className="absolute inset-x-0 bottom-0 z-20 overflow-hidden border-t border-white/10 bg-[var(--plum)]/70 py-3 text-[var(--cream)] backdrop-blur" aria-hidden="true" data-testid="hero-ticker">
      <div className="ticker-track">{row("a")}{row("b")}</div>
    </div>
  );
}
